import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Plus, Sparkles, Info, Lock, Unlock, X } from "lucide-react";

const SwipeableOutcomeRow = ({
  outcome,
  index,
  color,
  onLabelChange,
  onProbabilityChange,
  onToggleLock,
  onRemove,
  canRemove,
  isDuplicate,
}) => {
  const barRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = useCallback(
    (clientX) => {
      if (!barRef.current || outcome.locked) return;
      const rect = barRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.round(
        Math.max(1, Math.min(99, (x / rect.width) * 100)),
      );
      onProbabilityChange(percentage);
    },
    [onProbabilityChange, outcome.locked],
  );

  const handleStart = (clientX) => {
    if (outcome.locked) return;
    setIsDragging(true);
    handleDrag(clientX);
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => handleDrag(e.clientX);
    const onTouchMove = (e) => {
      e.preventDefault();
      handleDrag(e.touches[0].clientX);
    };
    const onEnd = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [isDragging, handleDrag]);

  return (
    <motion.div
      key={outcome.id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20, height: 0 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
        delay: index * 0.05,
      }}
      className="mainoutcome"
    >
      <div className="outcometop">
        <div className="outcometopleft">
          <span className="circle" style={{ backgroundColor: color }}></span>
          <input
            value={outcome.label}
            onChange={(e) => onLabelChange(e.target.value)}
            placeholder="Outcome label"
            className="gm-oe-label-input"
            style={isDuplicate ? { color: "hsl(0 84% 60%)" } : {}}
          />
          {isDuplicate && (
            <span className="gm-oe-duplicate-tag">Duplicate</span>
          )}
        </div>
        <div className="outcometopright">
          <button
            onClick={() => onToggleLock()}
            className={`gm-oe-lock-btn ${outcome.locked ? "gm-oe-lock-btn--active" : ""}`}
          >
            {outcome.locked ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.6667 7.3335H3.33333C2.59695 7.3335 2 7.93045 2 8.66683V13.3335C2 14.0699 2.59695 14.6668 3.33333 14.6668H12.6667C13.403 14.6668 14 14.0699 14 13.3335V8.66683C14 7.93045 13.403 7.3335 12.6667 7.3335Z"
                  stroke="#777777"
                  stroke-width="1.256"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4.66663 7.3335V4.66683C4.66663 3.78277 5.01782 2.93493 5.64294 2.30981C6.26806 1.68469 7.1159 1.3335 7.99996 1.3335C8.88401 1.3335 9.73186 1.68469 10.357 2.30981C10.9821 2.93493 11.3333 3.78277 11.3333 4.66683V7.3335"
                  stroke="#777777"
                  stroke-width="1.256"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.6667 7.3335H3.33333C2.59695 7.3335 2 7.93045 2 8.66683V13.3335C2 14.0699 2.59695 14.6668 3.33333 14.6668H12.6667C13.403 14.6668 14 14.0699 14 13.3335V8.66683C14 7.93045 13.403 7.3335 12.6667 7.3335Z"
                  stroke="#777777"
                  stroke-width="1.256"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4.66669 7.3335V4.66683C4.66669 3.78277 5.01788 2.93493 5.643 2.30981C6.26812 1.68469 7.11597 1.3335 8.00002 1.3335C8.88408 1.3335 9.73192 1.68469 10.357 2.30981C10.9822 2.93493 11.3334 3.78277 11.3334 4.66683"
                  stroke="#777777"
                  stroke-width="1.256"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            )}
          </button>

          {canRemove && (
            <button onClick={() => onRemove()} className="gm-oe-remove-btn">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3334 14.6667H4.66671C4.31309 14.6667 3.97395 14.5262 3.7239 14.2761C3.47385 14.0261 3.33337 13.687 3.33337 13.3333V4"
                  stroke="#DAD9D9"
                  stroke-width="1.26"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2 4H14"
                        stroke="#DAD9D9"
                  stroke-width="1.26"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M5.33337 3.99992V2.66659C5.33337 2.31296 5.47385 1.97382 5.7239 1.72378C5.97395 1.47373 6.31309 1.33325 6.66671 1.33325H9.33337C9.687 1.33325 10.0261 1.47373 10.2762 1.72378C10.5262 1.97382 10.6667 2.31296 10.6667 2.66659V3.99992"
                     stroke="#DAD9D9"
                  stroke-width="1.26"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div
        ref={barRef}
        className={`gm-oe-bar ${
          outcome.locked
            ? "gm-oe-bar--locked"
            : isDragging
              ? "gm-oe-bar--dragging"
              : "gm-oe-bar--idle"
        }`}
        onMouseDown={(e) => {
          if (e.target.closest("input, button")) return;
          handleStart(e.clientX);
        }}
        onTouchStart={(e) => {
          if (e.target.closest("input, button")) return;
          handleStart(e.touches[0].clientX);
        }}
      >
        <motion.div
          className="gm-oe-fill"
          animate={{ width: `${outcome.probability}%` }}
          transition={{ duration: isDragging ? 0.05 : 0.3 }}
          style={{ backgroundColor: color }}
        />

        {!outcome.locked && (
          <motion.div
            className="gm-oe-drag-indicator"
            animate={{ left: `calc(${outcome.probability}% - 4px)` }}
            transition={{ duration: isDragging ? 0.05 : 0.3 }}
          />
        )}

        <div className="gm-oe-inner">
          <span className="gm-oe-pct">{outcome.probability}%</span>
        </div>
      </div>
    </motion.div>
  );
};

export const FuturisticOutcomesEditor = ({
  outcomes: initialOutcomes,
  onConfirm,
}) => {
  const [outcomes, setOutcomes] = useState(initialOutcomes);

  const updateOutcome = (id, updates) => {
    setOutcomes((prev) =>
      prev.map((o) => {
        if (o.id !== id) return o;
        if (o.isUnclassified) return o;
        return { ...o, ...updates };
      }),
    );
  };

  const toggleLock = (id) => {
    setOutcomes((prev) =>
      prev.map((o) =>
        o.id === id && !o.isUnclassified ? { ...o, locked: !o.locked } : o,
      ),
    );
  };

  const addOutcome = () => {
    const hasEmptyOutcome = outcomes.some(
      (o) => !o.isUnclassified && !o.label.trim(),
    );
    if (hasEmptyOutcome) return;

    setOutcomes((prev) => {
      const unclassified = prev.filter((o) => o.isUnclassified);
      const locked = prev.filter((o) => o.locked && !o.isUnclassified);
      const unlocked = prev.filter((o) => !o.locked && !o.isUnclassified);

      const fixedTotal =
        unclassified.reduce((s, o) => s + o.probability, 0) +
        locked.reduce((s, o) => s + o.probability, 0);
      const available = 100 - fixedTotal;
      const newCount = unlocked.length + 1;
      const share = Math.floor(available / newCount);
      const remainder = available - share * newCount;

      const redistributed = unlocked.map((o, i) => ({
        ...o,
        probability: share + (i < remainder ? 1 : 0),
      }));
      const usedByRedistributed = redistributed.reduce(
        (s, o) => s + o.probability,
        0,
      );
      const newOutcome = {
        id: crypto.randomUUID(),
        label: "",
        probability: available - usedByRedistributed,
      };

      return [...locked, ...redistributed, newOutcome, ...unclassified];
    });
  };

  const removeOutcome = (id) => {
    const outcome = outcomes.find((o) => o.id === id);
    if (outcome?.isUnclassified) return;
    const nonUnclassifiedCount = outcomes.filter(
      (o) => !o.isUnclassified,
    ).length;
    if (nonUnclassifiedCount <= 2) return;
    setOutcomes((prev) => {
      const removed = prev.find((o) => o.id === id);
      if (!removed) return prev;
      const remaining = prev.filter((o) => o.id !== id);
      const freedProb = removed.probability;
      if (freedProb === 0) return remaining;
      const unlocked = remaining.filter((o) => !o.locked && !o.isUnclassified);
      if (unlocked.length === 0) return remaining;

      const lockedTotal = remaining
        .filter((o) => o.locked || o.isUnclassified)
        .reduce((s, o) => s + o.probability, 0);
      const available = 100 - lockedTotal;
      const equalShare = Math.floor(available / unlocked.length);
      const lastShare = available - equalShare * (unlocked.length - 1);

      const unlockedIds = unlocked.map((o) => o.id);
      const result = remaining.map((o) => {
        const idx = unlockedIds.indexOf(o.id);
        if (idx < 0) return o;
        return {
          ...o,
          probability:
            idx === unlockedIds.length - 1
              ? Math.max(1, lastShare)
              : Math.max(1, equalShare),
        };
      });

      const total = result.reduce((s, o) => s + o.probability, 0);
      const diff = 100 - total;
      if (diff !== 0) {
        const lastId = unlockedIds[unlockedIds.length - 1];
        return result.map((o) =>
          o.id === lastId ? { ...o, probability: o.probability + diff } : o,
        );
      }
      return result;
    });
  };

  const handleProbabilityChange = (id, value) => {
    const target = outcomes.find((o) => o.id === id);
    if (!target || target.locked || target.isUnclassified) return;

    setOutcomes((prev) => {
      const lockedTotal = prev.reduce(
        (sum, o) =>
          (o.locked || o.isUnclassified) && o.id !== id
            ? sum + o.probability
            : sum,
        0,
      );
      const unlockedOthers = prev.filter(
        (o) => o.id !== id && !o.locked && !o.isUnclassified,
      );
      const maxAllowed = 100 - lockedTotal - unlockedOthers.length;
      const newValue = Math.min(maxAllowed, Math.max(1, value));

      let remainingPercentage = 100 - lockedTotal - newValue;
      const updated = prev.map((o) => {
        if (o.id === id) return { ...o, probability: newValue };
        return o;
      });

      if (unlockedOthers.length > 0) {
        const minRemaining = unlockedOthers.length;
        const distributable = Math.max(0, remainingPercentage - minRemaining);
        const equalShare = Math.floor(distributable / unlockedOthers.length);
        const lastShare =
          remainingPercentage - equalShare * (unlockedOthers.length - 1);

        const unlockedIds = unlockedOthers.map((o) => o.id);
        const result = updated.map((o) => {
          const idx = unlockedIds.indexOf(o.id);
          if (idx < 0) return o;
          return {
            ...o,
            probability:
              idx === unlockedIds.length - 1
                ? Math.max(1, lastShare)
                : Math.max(1, equalShare),
          };
        });
        const total = result.reduce((s, o) => s + o.probability, 0);
        const diff = 100 - total;
        if (diff !== 0) {
          const lastUnlockedId = unlockedIds[unlockedIds.length - 1];
          return result.map((o) =>
            o.id === lastUnlockedId
              ? { ...o, probability: o.probability + diff }
              : o,
          );
        }
        return result;
      }

      return updated;
    });
  };

  const totalProbability = outcomes.reduce((sum, o) => sum + o.probability, 0);
  const nonUnclassifiedCount = outcomes.filter((o) => !o.isUnclassified).length;

  const getDuplicateLabels = () => {
    const labels = outcomes
      .filter((o) => !o.isUnclassified && o.label.trim())
      .map((o) => o.label.trim().toLowerCase());
    const seen = new Set();
    const dupes = new Set();
    for (const l of labels) {
      if (seen.has(l)) dupes.add(l);
      seen.add(l);
    }
    return dupes;
  };
  const duplicateLabels = getDuplicateLabels();
  const hasDuplicates = duplicateLabels.size > 0;

  const getOutcomeColor = (index, isUnclassified) => {
    if (isUnclassified) return "hsl(var(--muted-foreground))";
    const colors = [
      "hsl(68 100% 50%)",
      "hsl(0 84% 60%)",
      "hsl(45 93% 47%)",
      "hsl(280 70% 55%)",
      "hsl(200 80% 50%)",
      "hsl(173 80% 40%)",
      "hsl(25 95% 53%)",
      "hsl(330 80% 55%)",
      "hsl(95 70% 42%)",
      "hsl(350 65% 45%)",
      "hsl(250 60% 55%)",
      "hsl(142 76% 36%)",
    ];
    return colors[index % colors.length];
  };

  const regularOutcomes = outcomes.filter((o) => !o.isUnclassified);
  const unclassifiedOutcome = outcomes.find((o) => o.isUnclassified);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="gm-oe"
    >
      <div className="gm-oe-bars">
        <AnimatePresence>
          {regularOutcomes.map((outcome, index) => (
            <SwipeableOutcomeRow
              key={outcome.id}
              outcome={outcome}
              index={index}
              color={getOutcomeColor(index)}
              onLabelChange={(label) => updateOutcome(outcome.id, { label })}
              onProbabilityChange={(value) =>
                handleProbabilityChange(outcome.id, value)
              }
              onToggleLock={() => toggleLock(outcome.id)}
              onRemove={() => removeOutcome(outcome.id)}
              canRemove={nonUnclassifiedCount > 2}
              isDuplicate={
                outcome.label.trim() !== "" &&
                duplicateLabels.has(outcome.label.trim().toLowerCase())
              }
            />
          ))}
        </AnimatePresence>

        {unclassifiedOutcome && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mainoutcome gm-oe-unclassified-card"
          >
            <div className="outcometop">
              <div className="outcometopleft">
                <span
                  className="circle"
                  style={{ backgroundColor: "#ffffff" }}
                ></span>
                <input
                  value={unclassifiedOutcome.label}
                  readOnly
                  disabled
                  className="gm-oe-label-input"
                />
              </div>
              <div className="outcometopright gm-oe-unclassified-actions">
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="gm-oe-lock-btn gm-oe-lock-btn--active"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.6667 7.3335H3.33333C2.59695 7.3335 2 7.93045 2 8.66683V13.3335C2 14.0699 2.59695 14.6668 3.33333 14.6668H12.6667C13.403 14.6668 14 14.0699 14 13.3335V8.66683C14 7.93045 13.403 7.3335 12.6667 7.3335Z"
                      stroke="#777777"
                      strokeWidth="1.256"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.66663 7.3335V4.66683C4.66663 3.78277 5.01782 2.93493 5.64294 2.30981C6.26806 1.68469 7.1159 1.3335 7.99996 1.3335C8.88401 1.3335 9.73186 1.68469 10.357 2.30981C10.9821 2.93493 11.3333 3.78277 11.3333 4.66683V7.3335"
                      stroke="#777777"
                      strokeWidth="1.256"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="gm-oe-remove-btn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3334 14.6667H4.66671C4.31309 14.6667 3.97395 14.5262 3.7239 14.2761C3.47385 14.0261 3.33337 13.687 3.33337 13.3333V4"
                      stroke="#DAD9D9"
                      strokeWidth="1.26"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 4H14"
                      stroke="#DAD9D9"
                      strokeWidth="1.26"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.33337 3.99992V2.66659C5.33337 2.31296 5.47385 1.97382 5.7239 1.72378C5.97395 1.47373 6.31309 1.33325 6.66671 1.33325H9.33337C9.687 1.33325 10.0261 1.47373 10.2762 1.72378C10.5262 1.97382 10.6667 2.31296 10.6667 2.66659V3.99992"
                      stroke="#DAD9D9"
                      strokeWidth="1.26"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="gm-oe-bar gm-oe-bar--locked gm-oe-unclassified-bar">
              <motion.div
                className="gm-oe-fill gm-oe-unclassified-fill"
                animate={{ width: `${unclassifiedOutcome.probability}%` }}
                transition={{ duration: 0.3 }}
              />
              <div className="gm-oe-inner">
                <span className="gm-oe-pct">
                  {unclassifiedOutcome.probability}%
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {outcomes.length < 12 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={addOutcome}
          className="gm-oe-add-btn"
        >
          Add Outcome
        </motion.button>
      )}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <button
          onClick={() => onConfirm(outcomes)}
          disabled={
            totalProbability !== 100 ||
            outcomes.some((o) => !o.label.trim()) ||
            hasDuplicates
          }
          className="gm-primary-btn"
        >
          Confirm Outcomes
        </button>
      </motion.div>
    </motion.div>
  );
};
